// Data layer. Screens only talk to `api`, never to fetch() directly.
// - mockApi: works with no backend (sample catalog + local state). Used for the preview.
// - httpApi: calls the Cloudflare Workers API (/v1) described in the blueprint.
// Switch with KN_API_MODE=http at build time once the backend is deployed.
import { EPISODES, PLAN, REELS, SHOWS, TOP10_IDS, TOPICS, seedNotifications } from './data';
import { getState, hasPlus, setState, subscribeStore } from './store';
import { rememberShows } from './showIndex';
import type {
  AppNotification, ContinueItem, HomeSection, Plan, PlayResult, Reel, Show, ShowDetail, Subscription, Topic, UpNextItem,
} from './types';

declare const __API_MODE__: 'mock' | 'http';
declare const __API_BASE__: string;

export interface Api {
  sendOtp(phone: string, channel: 'sms' | 'whatsapp'): Promise<{ resendInS: number }>;
  verifyOtp(phone: string, code: string): Promise<{ userId: string; name: string }>;
  home(): Promise<HomeSection[]>;
  topics(): Promise<Topic[]>;
  showsByTopic(slug: string): Promise<Show[]>;
  show(slugOrId: string): Promise<ShowDetail | null>;
  search(q: string): Promise<Show[]>;
  reels(): Promise<Reel[]>;
  /** Similar episodes to offer when an episode ends (GET /v1/recommendations/up-next). */
  upNext(showId: string, episodeNo: number): Promise<UpNextItem[]>;
  play(showId: string, episodeNo: number): Promise<PlayResult>;
  heartbeat(input: { showId: string; episodeId: string; positionS: number; durationS: number }): Promise<void>;
  plan(): Promise<Plan>;
  startSubscription(upiApp: string): Promise<{ checkoutId: string }>;
  confirmMandate(checkoutId: string, approved: boolean): Promise<Subscription>;
  cancelSubscription(): Promise<Subscription>;
  notifications(): Promise<AppNotification[]>;
  /** Live API: restore the session on start-up and pull account data. Mock: nothing to do. */
  bootstrap(): Promise<void>;
  deleteAccount(): Promise<void>;
  /** Contact and Become a creator forms on the website. */
  submitForm(kind: 'contact' | 'creator', body: Record<string, string>): Promise<{ message: string }>;
  registerPush(token: string, platform: 'android' | 'ios' | 'web'): Promise<void>;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Short anonymous code like "KN 7F3A-21C9" derived from the user id (the real backend issues a per-session code). */
function viewerCode(userId = 'guest'): string {
  let h = 2166136261;
  for (const ch of userId + ':kn') { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  const hex = (h >>> 0).toString(16).toUpperCase().padStart(8, '0');
  return `KN ${hex.slice(0, 4)}-${hex.slice(4)}`;
}
const DAY = 86400_000;

function byId(id: string) {
  return SHOWS.find((s) => s.id === id || s.slug === id) || null;
}

function normalize(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9₹]/g, '');
}

function continueItems(): ContinueItem[] {
  const { progress } = getState();
  return Object.entries(progress)
    .filter(([, p]) => p.positionS > 5 && p.positionS < p.durationS - 5)
    .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
    .map(([episodeId, p]) => {
      const episode = EPISODES.find((e) => e.id === episodeId);
      const show = episode && byId(episode.showId);
      return episode && show ? { show, episode, positionS: p.positionS } : null;
    })
    .filter((x): x is ContinueItem => !!x)
    .slice(0, 8);
}

export const mockApi: Api = {
  async sendOtp() {
    await wait(400);
    return { resendInS: 30 };
  },
  async verifyOtp(phone, code) {
    await wait(500);
    if (!/^\d{6}$/.test(code)) throw new Error('Enter all 6 digits of the code.');
    if (code === '000000') throw new Error('That code is not right. Check the SMS and try again.');
    return { userId: 'u_' + phone.slice(-4), name: 'Rahul' };
  },
  async home() {
    await wait(250);
    const s = getState();
    const shows = (ids: string[]) => ids.map((id) => byId(id)!).filter(Boolean);
    const lastWatched = continueItems()[0]?.show;
    const liked = new Set(s.interests);
    const forYouPool = SHOWS.filter((x) => (liked.size ? liked.has(x.topic) : true) && x.id !== lastWatched?.id);
    const forYou = (forYouPool.length >= 4 ? forYouPool : SHOWS).slice(0, 8);
    const sections: HomeSection[] = [
      { kind: 'featured', title: 'Featured', items: shows(['s16', 's18', 's1']) },
    ];
    const cont = continueItems();
    if (cont.length) sections.push({ kind: 'continue', title: 'Continue watching', items: cont });
    sections.push({ kind: 'top10', title: 'Top 10 on Kuch Naya', subtitle: 'This week', items: shows(TOP10_IDS) });
    sections.push({ kind: 'rail', title: 'For you', subtitle: lastWatched ? `Because you watched “${lastWatched.title}”` : 'Picked from your topics', items: forYou });
    sections.push({ kind: 'rail', title: 'New this week', items: SHOWS.filter((x) => x.isNew) });
    sections.push({ kind: 'rail', title: 'Free to watch', subtitle: 'No Plus needed', items: SHOWS.filter((x) => x.access === 'free') });
    sections.push({ kind: 'rail', title: 'Micro dramas', subtitle: 'Full stories in 2-minute episodes', items: SHOWS.filter((x) => x.topic === 'drama') });
    sections.push({ kind: 'rail', title: 'College life', items: SHOWS.filter((x) => x.topic === 'college') });
    sections.push({ kind: 'rail', title: 'Trending in Govt Services', items: SHOWS.filter((x) => x.topic === 'govt') });
    return sections;
  },
  async topics() {
    await wait(120);
    return TOPICS;
  },
  async showsByTopic(slug) {
    await wait(180);
    return slug === 'all' ? SHOWS : SHOWS.filter((s) => s.topic === slug);
  },
  async show(slugOrId) {
    await wait(180);
    const show = byId(slugOrId);
    if (!show) return null;
    return { ...show, episodes: EPISODES.filter((e) => e.showId === show.id) };
  },
  async search(q) {
    await wait(150);
    const n = normalize(q);
    if (!n) return [];
    return SHOWS.filter((s) => {
      const topic = TOPICS.find((t) => t.slug === s.topic)?.name || '';
      return normalize(s.title + s.description + topic).includes(n);
    });
  },
  async upNext(showId) {
    await wait(120);
    const st = getState();
    const current = byId(showId)!;
    const doneWith = (sid: string) => EPISODES.filter((e) => e.showId === sid).every((e) => (st.progress[e.id]?.positionS ?? 0) >= e.durationS - 5);
    const score = (s: Show) => (s.topic === current.topic ? 3 : 0) + (st.interests.includes(s.topic) ? 2 : 0) + (TOP10_IDS.includes(s.id) ? 1 : 0);
    const items = SHOWS.filter((s) => s.id !== current.id && !doneWith(s.id))
      .sort((a, b) => score(b) - score(a))
      .map((s) => {
        const eps = EPISODES.filter((e) => e.showId === s.id);
        // Continue where they left off in that show, else episode 1.
        const episode = eps.find((e) => (st.progress[e.id]?.positionS ?? 0) < e.durationS - 5) || eps[0];
        const locked = s.access === 'plus' && !episode.isFreePreview && !hasPlus();
        const topic = TOPICS.find((t) => t.slug === s.topic)?.name || '';
        return { show: s, episode, locked, reason: s.topic === current.topic ? topic : TOP10_IDS.includes(s.id) ? 'Popular' : topic };
      });
    // Playable ones first, keep at most 8.
    return [...items.filter((i) => !i.locked), ...items.filter((i) => i.locked)].slice(0, 8);
  },
  async reels() {
    await wait(200);
    return REELS.map((r) => ({ ...r, locked: r.locked && !hasPlus() }));
  },
  async play(showId, episodeNo) {
    await wait(150);
    const show = byId(showId)!;
    const episode = EPISODES.find((e) => e.showId === show.id && e.no === episodeNo)!;
    // Server rule from the blueprint: locked = plus show && no active sub && not a free preview.
    const locked = show.access === 'plus' && !episode.isFreePreview && !hasPlus();
    if (locked) return { ok: false, reason: 'locked', show, episode };
    // Privacy: the on-screen mark is an anonymous viewer code, never a name or phone number.
    // The server keeps the code → account mapping, so a leaked recording can still be traced.
    return { ok: true, show, episode, playlistUrl: episode.hlsUrl || null, watermark: viewerCode(getState().session?.userId) };
  },
  async heartbeat({ showId, episodeId, positionS, durationS }) {
    setState((s) => ({ progress: { ...s.progress, [episodeId]: { showId, positionS, durationS, updatedAt: Date.now() } } }));
  },
  async plan() {
    return PLAN;
  },
  async startSubscription() {
    await wait(500);
    return { checkoutId: 'chk_' + Math.random().toString(36).slice(2, 8) };
  },
  async confirmMandate(_checkoutId, approved) {
    await wait(900);
    if (!approved) throw new Error('AutoPay was not approved in your UPI app. You have not been charged.');
    const now = Date.now();
    const s = getState();
    const sub: Subscription = {
      status: 'trial',
      planId: PLAN.id,
      upiApp: (s.sub && s.sub.upiApp) || 'UPI',
      vpaMasked: `98XXXX${(s.session?.phone || '0000').slice(-4)}@upi`,
      mandateId: 'KN-UPI-' + Math.random().toString(36).slice(2, 8).toUpperCase(),
      startedAt: now,
      currentPeriodEnd: now + PLAN.introDays * DAY,
      nextChargePaise: PLAN.pricePaise,
    };
    return sub;
  },
  async cancelSubscription() {
    await wait(600);
    const sub = getState().sub!;
    return { ...sub, status: 'cancelled' };
  },
  async notifications() {
    await wait(150);
    const s = getState();
    return [...s.extraNotifs, ...seedNotifications(Date.now())].sort((a, b) => b.at - a.at);
  },
  async bootstrap() {},
  async registerPush() {},
  async deleteAccount() { await wait(400); },
  async submitForm(kind) {
    await wait(500);
    return { message: kind === 'creator' ? 'Thanks! If your work is a good fit, our content team will call you.' : 'Thanks! Our team will reply within 2 working days.' };
  },
};

// ---- Real backend (Cloudflare Workers, /v1). Same contract as mockApi. ----
// Access token (15 min) lives in memory only. The refresh token is an HttpOnly cookie on the web;
// in the Capacitor app it is kept by the app (x-kn-client: app) — see lib/session.ts.
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const isNative = () => !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
const nativeToken = {
  get: () => { try { return localStorage.getItem('kn-rt'); } catch { return null; } },
  set: (t?: string) => { try { if (t) localStorage.setItem('kn-rt', t); else localStorage.removeItem('kn-rt'); } catch { /* ignore */ } },
};

interface ServerUser { id: string; name: string; phone: string; interests: string[]; videoLang: 'Hindi' | 'English' | 'Both'; settings: Record<string, unknown>; plus: boolean; plusUntil: number | null }

async function raw(path: string, init: RequestInit = {}) {
  return fetch(__API_BASE__ + path, {
    ...init,
    credentials: isNative() ? 'omit' : 'include',   // native apps keep the refresh token themselves
    headers: {
      'content-type': 'application/json',
      ...(isNative() ? { 'x-kn-client': 'app' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...init.headers,
    },
  });
}

/** Swap the refresh token for a new access token (one request at a time). */
function refresh(): Promise<boolean> {
  refreshing ||= (async () => {
    try {
      const r = await raw('/auth/refresh', { method: 'POST', body: JSON.stringify(isNative() ? { refreshToken: nativeToken.get() } : {}) });
      if (!r.ok) { accessToken = null; return false; }
      const b = await r.json() as { accessToken: string; refreshToken?: string; user: ServerUser };
      accessToken = b.accessToken;
      if (b.refreshToken) nativeToken.set(b.refreshToken);
      applyUser(b.user);
      return true;
    } catch { return false; } finally { setTimeout(() => { refreshing = null; }, 0); }
  })();
  return refreshing;
}

async function call<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  let res: Response;
  try { res = await raw(path, init); }
  catch { throw new Error('No internet connection. Check your network and try again.'); }
  if (res.status === 401 && !retried && getState().session) {
    if (await refresh()) return call<T>(path, init, true);
    setState({ session: null, sub: null });   // session ended elsewhere (logged out, account deleted)
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error?.message || 'Something went wrong. Please try again.');
  return body as T;
}

function applyUser(u: ServerUser) {
  const s = getState();
  if (!s.session) return;
  setState({ session: { ...s.session, userId: u.id, name: u.name, phone: u.phone } });
}

/** Pull the account (subscription, saves, likes, progress, settings) into the local store. */
async function hydrate() {
  const [me, prog] = await Promise.all([
    call<{ user: ServerUser; subscription: Subscription | null; saves: string[]; likes: string[] }>('/me'),
    call<{ progress: { episodeId: string; showId: string; positionS: number; durationS: number; updatedAt: number }[] }>('/me/progress'),
  ]);
  const s = getState();
  syncPaused = true;
  setState({
    session: s.session ? { ...s.session, name: me.user.name, phone: me.user.phone, userId: me.user.id } : s.session,
    sub: me.subscription,
    saves: Object.fromEntries(me.saves.map((id) => [id, true as const])),
    likes: Object.fromEntries(me.likes.map((id) => [id, true as const])),
    interests: me.user.interests.length ? me.user.interests : s.interests,
    onboarded: s.onboarded || me.user.interests.length > 0,
    videoLang: me.user.videoLang,
    progress: Object.fromEntries(prog.progress.map((p) => [p.episodeId, { showId: p.showId, positionS: p.positionS, durationS: p.durationS, updatedAt: p.updatedAt }])),
    settings: { ...s.settings, ...(me.user.settings as Partial<typeof s.settings>) },
  });
  syncPaused = false;
  snapshot = getState();
}

// ---- Store → server sync (saves, likes, profile, read notifications, logout) ----
let syncPaused = false;
let snapshot = getState();
let profileTimer: number | undefined;
function startSync() {
  subscribeStore(() => {
    if (syncPaused) return;
    const prev = snapshot;
    const next = getState();
    snapshot = next;
    if (prev.session && !next.session) {   // logged out (or account deleted)
      raw('/auth/logout', { method: 'POST', body: JSON.stringify(isNative() ? { refreshToken: nativeToken.get() } : {}) }).catch(() => {});
      accessToken = null; nativeToken.set();
      return;
    }
    if (!next.session || !accessToken) return;
    const diff = (a: Record<string, true>, b: Record<string, true>, path: string) => {
      for (const id of Object.keys(b)) if (!a[id]) call(`${path}/${encodeURIComponent(id)}`, { method: 'PUT' }).catch(() => {});
      for (const id of Object.keys(a)) if (!b[id]) call(`${path}/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => {});
    };
    if (prev.saves !== next.saves) diff(prev.saves, next.saves, '/me/saves');
    if (prev.likes !== next.likes) diff(prev.likes, next.likes, '/me/likes');
    if (prev.readNotifs !== next.readNotifs) {
      const ids = Object.keys(next.readNotifs).filter((id) => !prev.readNotifs[id] && next.notifIds?.includes(id));
      if (ids.length) call('/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) }).catch(() => {});
    }
    if (prev.interests !== next.interests || prev.videoLang !== next.videoLang || prev.settings !== next.settings || prev.session?.name !== next.session?.name) {
      window.clearTimeout(profileTimer);
      profileTimer = window.setTimeout(() => {
        const s = getState();
        call('/me', { method: 'PATCH', body: JSON.stringify({ name: s.session?.name, interests: s.interests, videoLang: s.videoLang, settings: s.settings }) }).catch(() => {});
      }, 600);
    }
  });
}

// Checkout ids that run against live Razorpay (Checkout handles the UPI app); others use the dev simulator.
const liveCheckouts = new Set<string>();
/** True when Razorpay Checkout handled the UPI step, so the approve screen just waits for the bank. */
export const isLiveCheckout = (id: string) => liveCheckouts.has(id);
interface RazorpayCtor { new (o: Record<string, unknown>): { open(): void } }
function loadRazorpay(): Promise<RazorpayCtor> {
  const w = window as unknown as { Razorpay?: RazorpayCtor };
  if (w.Razorpay) return Promise.resolve(w.Razorpay);
  return new Promise((ok, fail) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => (w.Razorpay ? ok(w.Razorpay) : fail(new Error('Payment page did not load.')));
    s.onerror = () => fail(new Error('Payment page did not load. Check your internet and try again.'));
    document.head.appendChild(s);
  });
}

let started = false;
export const httpApi: Api = {
  async bootstrap() {
    if (!started) { started = true; startSync(); }
    if (!getState().session) return;
    if (await refresh()) await hydrate().catch(() => {});
    else if (navigator.onLine) setState({ session: null, sub: null });
    httpApi.notifications().catch(() => {});
  },
  sendOtp: (phone, channel) => call('/auth/otp/send', { method: 'POST', body: JSON.stringify({ phone, channel }) }),
  verifyOtp: async (phone, code) => {
    const r = await call<{ accessToken: string; refreshToken?: string; user: ServerUser }>('/auth/otp/verify', { method: 'POST', body: JSON.stringify({ phone, code }) });
    accessToken = r.accessToken;
    if (r.refreshToken) nativeToken.set(r.refreshToken);
    // Pull the account first so a returning person skips onboarding on a new phone.
    await hydrate().catch(() => {});
    setTimeout(() => { httpApi.notifications().catch(() => {}); }, 0);
    return { userId: r.user.id, name: r.user.name };
  },
  home: () => call('/home'),
  topics: () => call('/categories'),
  showsByTopic: (slug) => call(`/categories/${slug}/shows`),
  show: async (id) => { try { return await call<ShowDetail>(`/shows/${encodeURIComponent(id)}`); } catch (e) { if (/could not find/i.test((e as Error).message)) return null; throw e; } },
  search: (q) => call(`/search?q=${encodeURIComponent(q)}`),
  reels: () => call('/reels'),
  upNext: (showId, episodeNo) => call(`/recommendations/up-next?show=${encodeURIComponent(showId)}&ep=${episodeNo}`),
  play: (showId, no) => call(`/shows/${encodeURIComponent(showId)}/episodes/${no}/play`),
  heartbeat: async (b) => {
    // Keep the local copy for instant "continue watching", send to the server when logged in.
    setState((s) => ({ progress: { ...s.progress, [b.episodeId]: { showId: b.showId, positionS: b.positionS, durationS: b.durationS, updatedAt: Date.now() } } }));
    if (getState().session) await call('/heartbeat', { method: 'POST', body: JSON.stringify(b), keepalive: true }).catch(() => {});
  },
  plan: () => call('/plans/plus_monthly'),
  async startSubscription(upiApp) {
    const r = await call<{ checkoutId: string; live: boolean; keyId: string | null }>('/subscriptions/razorpay', { method: 'POST', body: JSON.stringify({ upiApp }) });
    if (r.live && r.keyId) {
      liveCheckouts.add(r.checkoutId);
      const Razorpay = await loadRazorpay();
      await new Promise<void>((ok, fail) => {
        new Razorpay({
          key: r.keyId, subscription_id: r.checkoutId, name: 'Kuch Naya', description: 'Kuch Naya Plus',
          prefill: { contact: '+91' + (getState().session?.phone || '') }, theme: { color: '#141414' },
          handler: () => ok(), modal: { ondismiss: () => fail(new Error('Payment was not completed. You have not been charged.')) },
        }).open();
      });
    }
    return { checkoutId: r.checkoutId };
  },
  async confirmMandate(checkoutId, approved) {
    if (!liveCheckouts.has(checkoutId)) await call('/dev/razorpay/simulate', { method: 'POST', body: JSON.stringify({ subscriptionId: checkoutId, approved }) });
    // Wait for the webhook to land (usually a few seconds after the bank confirms).
    for (let i = 0; i < 60; i++) {
      const r = await call<{ status: 'pending' | 'active' | 'failed'; subscription?: Subscription; message?: string }>(`/subscriptions/checkout/${checkoutId}`);
      if (r.status === 'failed') throw new Error(r.message || 'AutoPay was not approved. You have not been charged.');
      if (r.status === 'active' && r.subscription) { await refresh(); return r.subscription; }
      await wait(i < 10 ? 1000 : 3000);
    }
    throw new Error('Your bank is taking longer than usual. If money was taken, Plus will switch on by itself within a few minutes.');
  },
  cancelSubscription: async () => { const sub = await call<Subscription>('/subscriptions/cancel', { method: 'POST' }); await refresh(); return sub; },
  async notifications() {
    const list = await call<(AppNotification & { read?: boolean })[]>('/notifications');
    syncPaused = true;
    setState((s) => ({
      notifIds: list.map((n) => n.id),
      readNotifs: { ...s.readNotifs, ...Object.fromEntries(list.filter((n) => n.read).map((n) => [n.id, true as const])) },
    }));
    syncPaused = false;
    snapshot = getState();
    return list;
  },
  async deleteAccount() {
    await call('/me', { method: 'DELETE' });
    accessToken = null; nativeToken.set();
  },
  submitForm: (kind, body) => call(kind === 'creator' ? '/creators/apply' : '/contact', { method: 'POST', body: JSON.stringify(body) }),
  registerPush: async (token, platform) => { if (getState().session) await call('/push/tokens', { method: 'POST', body: JSON.stringify({ token, platform }) }); },
};

// Every result passes through the show index, so show pages can paint instantly from list data.
function withShowIndex(a: Api): Api {
  const wrapped = { ...a } as Api;
  (['home', 'showsByTopic', 'search', 'upNext', 'show'] as const).forEach((k) => {
    const fn = a[k] as (...args: unknown[]) => Promise<unknown>;
    (wrapped as unknown as Record<string, unknown>)[k] = (...args: unknown[]) => fn(...args).then((r) => { rememberShows(r); return r; });
  });
  return wrapped;
}

export const api: Api = withShowIndex(__API_MODE__ === 'http' ? httpApi : mockApi);
